import { useDeviceStore } from '@/stores/useDeviceStore';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Archive } from 'lucide-react';
import type { DeviceStatus } from '@/types';

const statusOptions: { value: DeviceStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
];

export function DeviceFilters() {
  const { filters, setStatusFilter, setOrganizationFilter, setShowArchived } = useDeviceStore();
  const devices = useDeviceStore((s) => s.devices);
  const organizations = Array.from(
    new Map(
      devices.map((d) => [d.organization_id, { id: d.organization_id, name: d.organization_name }])
    ).values()
  );
  const archivedCount = devices.filter((d) => d.archived_at != null).length;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1">
        {statusOptions.map((opt) => (
          <Button
            key={opt.value}
            variant="ghost"
            size="sm"
            className={cn(
              'h-7 text-xs',
              filters.status === opt.value &&
                !filters.showArchived &&
                'bg-brand-purple-subtle text-brand-purple'
            )}
            onClick={() => {
              setStatusFilter(opt.value);
              if (filters.showArchived) setShowArchived(false);
            }}
          >
            {opt.label}
          </Button>
        ))}
        {archivedCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'ml-auto h-7 gap-1 text-xs',
              filters.showArchived && 'bg-brand-purple-subtle text-brand-purple'
            )}
            onClick={() => setShowArchived(!filters.showArchived)}
            title={filters.showArchived ? 'Show active devices' : 'Show archived devices'}
          >
            <Archive className="h-3 w-3" />
            Archived ({archivedCount})
          </Button>
        )}
      </div>

      {organizations.length > 1 && (
        <Select
          value={filters.organizationId}
          onValueChange={(value) => setOrganizationFilter(value ?? 'all')}
        >
          <SelectTrigger className="h-8 text-xs">
            {/* Base UI renders the raw value by default, so map the id → name here. */}
            <SelectValue placeholder="All Organizations">
              {(value) =>
                value == null || value === 'all'
                  ? 'All Organizations'
                  : (organizations.find((o) => o.id === value)?.name ?? value)
              }
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Organizations</SelectItem>
            {organizations.map((o) => (
              <SelectItem key={o.id} value={o.id}>
                {o.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
